import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import SEOHead from "@/components/SEOHead";
import Breadcrumbs from "@/components/Breadcrumbs";
import ProductCheckout from "@/components/ProductCheckout";
import { breadcrumbLd } from "@/lib/seo";
import { CheckCircle2, ArrowRight, Sparkles, ShieldCheck, Clock, FileDown, UserCheck, MessageCircle, Loader2 } from "lucide-react";

const ROUTE_TO_KEY = {
  "regret-risk-report": "REGRET_RISK_99",
  "expert-call": "EXPERT_CALL_499",
  "decision-pack": "DECISION_PACK_999",
};

const INCLUSIONS = {
  REGRET_RISK_99: [
    "Personalised College Regret Risk Score™",
    "Full explanation of your score",
    "Dream / Target / Safe shortlist (up to 5)",
    "Key decision risks and what to verify",
    "Unique order reference and downloadable PDF",
    "Delivered instantly after payment",
  ],
  EXPERT_CALL_499: [
    "20-minute 1-on-1 call with an admission expert",
    "Personal profile and preferences review",
    "Human-reviewed Dream / Target / Safe shortlist",
    "Concise next-step summary after the call",
    "You pick your slot (IST, Mon–Sat, 10:00–19:00)",
    "Booking confirmed only after verified payment",
  ],
  DECISION_PACK_999: [
    "30-minute expert admission-strategy call",
    "Personalised Dream / Target / Safe shortlist",
    "Comparison of up to 5 colleges",
    "Verified fee, cutoff and placement indicators where available",
    "Risk flags, priority order and admission checklist",
    "Final human-reviewed PDF within 24 hours of the call",
  ],
};

const WHO_IS_IT_FOR = {
  REGRET_RISK_99: "Students who want a fast, honest AI-assisted sanity check on their current college choices — for less than a coffee.",
  EXPERT_CALL_499: "Students who want a real human to review their AI recommendations and clarify course, budget and location trade-offs.",
  DECISION_PACK_999: "Students making a final admission decision who want a written, human-reviewed plan they can act on with confidence.",
};

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const productKey = ROUTE_TO_KEY[slug];
  const [product, setProduct] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!productKey) return;
    api.get("/products").then((r) => {
      const p = (r.data.products || []).find((x) => x.key === productKey);
      setProduct(p);
      track("product_view", { product_key: productKey });
    });
  }, [productKey]);

  if (!productKey) {
    // In-app soft-404 — mark noindex, keep branded shell.
    return (
      <>
        <SEOHead title="Product not found" description="This product does not exist." canonicalPath={`/products/${slug || ""}`} noindex />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Product not found</h1>
          <p className="mt-2 text-slate-500">The product you are looking for doesn't exist.</p>
          <button onClick={() => navigate("/pricing")} className="mt-6 rounded-lg bg-[#0B2A6B] px-5 py-2.5 text-sm font-bold text-white">See all products</button>
        </div>
      </>
    );
  }

  if (!product) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#0B2A6B]" /></div>;
  }

  const path = `/products/${slug}`;
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Pricing", path: "/pricing" },
    { name: product.name, path },
  ];

  return (
    <>
      <SEOHead
        title={`${product.name} — ₹${product.price}`}
        description={product.deliverable}
        canonicalPath={path}
        jsonLd={[breadcrumbLd(crumbs), {
          "@context": "https://schema.org", "@type": "Product",
          name: product.name, description: product.deliverable,
          offers: { "@type": "Offer", price: product.price, priceCurrency: "INR", availability: "https://schema.org/InStock" },
        }]}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumbs items={crumbs} />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-16">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
          <p className="inline-flex items-center gap-2 rounded-full bg-[#FDF9F1] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#C08E3B]">
            <Sparkles className="h-3.5 w-3.5" /> {product.tagline}
          </p>
          <h1 className="mt-3 font-display text-3xl sm:text-5xl font-extrabold text-slate-900 leading-tight">{product.name}</h1>
          <p className="mt-3 text-slate-600 max-w-2xl">{product.deliverable}</p>

          <div className="mt-6 flex flex-wrap items-baseline gap-4">
            <span className="font-display text-4xl sm:text-5xl font-extrabold text-[#0B2A6B]">₹{product.price}</span>
            <span className="text-sm text-slate-500">one-time · inclusive of applicable taxes</span>
          </div>

          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">What's included</h2>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                {(INCLUSIONS[productKey] || []).map((line) => (
                  <li key={line} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{line}</li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">Who is this for</h2>
              <p className="mt-3 text-sm text-slate-700">{WHO_IS_IT_FOR[productKey]}</p>
              <h2 className="mt-6 text-xs font-bold uppercase tracking-wider text-slate-500">Delivery</h2>
              <p className="mt-2 text-sm text-slate-700 flex items-center gap-1"><Clock className="h-4 w-4 text-slate-400" /> {product.delivery}</p>
              <h2 className="mt-6 text-xs font-bold uppercase tracking-wider text-slate-500">Safety & terms</h2>
              <ul className="mt-2 space-y-1 text-xs text-slate-500">
                <li className="flex gap-2"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Secure Cashfree payment. No hidden charges.</li>
                <li className="flex gap-2"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" /> No admission guarantee — this is decision support, not application submission.</li>
                <li className="flex gap-2"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Refunds handled per our published policy.</li>
              </ul>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <button data-testid={`product-cta-${productKey.toLowerCase()}`} onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-[#0B2A6B] px-6 py-3.5 text-sm font-bold text-white hover:bg-[#081E4D]">
              {product.cta} <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="fallback-fit-check" onClick={() => navigate("/ai-counseling")}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-6 py-3.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Try the free AI College Fit Check first
            </button>
          </div>
        </div>
      </div>

      <ProductCheckout open={open} onClose={() => setOpen(false)} productKey={productKey} />
    </>
  );
}
